import React, { useState, useEffect } from 'react';
import './Settings.css';
import BabyManagement from '../components/BabyManagement';
import AccountSharing from '../components/AccountSharing';
import ActiveBabySelector from '../components/ActiveBabySelector';
import { API_BASE_URL } from '../config';
import { Trash, Download, Warning } from 'phosphor-react';
import { useToast } from '../contexts/ToastContext';

interface SettingsProps {
  user: any;
  onLogout: () => void;
  onUserUpdate: (userData: any) => void;
}

const Settings: React.FC<SettingsProps> = ({ user, onLogout, onUserUpdate }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [sleepCount, setSleepCount] = useState(0);
  const [activeBaby, setActiveBaby] = useState<any>(null);
  const { showToast } = useToast();

  // Email verification state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isRequestingCode, setIsRequestingCode] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [codeRequested, setCodeRequested] = useState(false);

  // Fetch active baby and sleep count
  useEffect(() => {
    const fetchActiveBaby = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/babies/user/${user.id}`);
        if (response.ok) {
          const babies = await response.json();
          const active = babies.find((b: any) => b.id === user.active_baby_id);
          setActiveBaby(active);

          if (active) {
            // Fetch sleep count for active baby
            const sleepResponse = await fetch(`${API_BASE_URL}/api/sleep/baby/${active.id}`);
            if (sleepResponse.ok) {
              const sleepEntries = await sleepResponse.json();
              setSleepCount(sleepEntries.length);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching active baby:', error);
      }
    };

    fetchActiveBaby();
  }, [user.id, user.active_baby_id]);

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      onLogout();
    }
  };

  const handleTimeFormatChange = async (use24h: boolean) => {
    setIsUpdating(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          time_format_24h: use24h
        }),
      });

      if (response.ok) {
        const updatedUser = await response.json();
        onUserUpdate(updatedUser);
      } else {
        console.error('Failed to update time format');
      }
    } catch (error) {
      console.error('Error updating time format:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteData = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmText !== 'DELETE' || !activeBaby) {
      return;
    }

    setIsDeleting(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/sleep/baby/${activeBaby.id}/all?user_id=${user.id}`,
        {
          method: 'DELETE',
        }
      );

      if (response.ok) {
        const result = await response.json();
        showToast(result.message, 'success');
        setShowDeleteModal(false);
        setDeleteConfirmText('');
        setSleepCount(0);
      } else {
        const error = await response.json();
        showToast(error.detail || 'Failed to delete sleep data', 'error');
      }
    } catch (error) {
      console.error('Error deleting sleep data:', error);
      showToast('Failed to delete sleep data', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleExportData = async () => {
    setIsExporting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/sleep/export/${user.id}`);

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `baby-sleep-data-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        showToast('Sleep data exported successfully', 'success');
      } else {
        const error = await response.json();
        showToast(error.detail || 'Failed to export sleep data', 'error');
      }
    } catch (error) {
      console.error('Error exporting sleep data:', error);
      showToast('Failed to export sleep data', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const handleRequestVerificationCode = async () => {
    if (!email.trim()) {
      showToast('Please enter an email address', 'error');
      return;
    }

    setIsRequestingCode(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${user.id}/request-email-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.trim() }),
      });

      if (response.ok) {
        setCodeRequested(true);
        showToast('Verification code sent to your email', 'success');
      } else {
        const error = await response.json();
        showToast(error.detail || 'Failed to send verification code', 'error');
      }
    } catch (error) {
      console.error('Error requesting verification code:', error);
      showToast('Failed to send verification code', 'error');
    } finally {
      setIsRequestingCode(false);
    }
  };

  const handleVerifyEmail = async () => {
    if (!verificationCode.trim()) {
      showToast('Please enter the verification code', 'error');
      return;
    }

    setIsVerifying(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${user.id}/verify-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          code: verificationCode.trim(),
        }),
      });

      if (response.ok) {
        const updatedUser = await response.json();
        onUserUpdate(updatedUser);
        localStorage.setItem('babysleep_user', JSON.stringify(updatedUser));
        showToast('Email verified successfully!', 'success');
        setShowEmailModal(false);
        setEmail('');
        setVerificationCode('');
        setCodeRequested(false);
      } else {
        const error = await response.json();
        showToast(error.detail || 'Failed to verify email', 'error');
      }
    } catch (error) {
      console.error('Error verifying email:', error);
      showToast('Failed to verify email', 'error');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="settings-page">
      <div className="settings-content">
        <h1>Settings</h1>

        <section className="settings-section">
          <h2>Account</h2>
          <div className="user-info">
            <p><strong>Username:</strong> {user.username}</p>
            <p><strong>Email:</strong> {user.email || 'Not set'}</p>
            <p><strong>Member since:</strong> {new Date(user.created_at).toLocaleDateString()}</p>
          </div>
        </section>

        {(user.is_anonymous || !user.email) && (
          <section className="settings-section anonymous-warning-section">
            <div className="anonymous-warning">
              <div className="warning-header">
                <Warning size={24} weight="fill" />
                <h3>Secure Your Data</h3>
              </div>
              <p>
                Your account doesn't have an email address. Without an email, you risk losing all your sleep data if:
              </p>
              <ul>
                <li>You clear your browser data</li>
                <li>You switch to a different device</li>
                <li>Your browser data gets corrupted</li>
              </ul>
              <p><strong>Add an email to:</strong></p>
              <ul>
                <li>Protect your data across devices</li>
                <li>Enable account sharing with your partner</li>
                <li>Recover your account if needed</li>
              </ul>
              <button
                onClick={() => setShowEmailModal(true)}
                className="add-email-btn"
              >
                Add Email Address
              </button>
            </div>
          </section>
        )}

        <section className="settings-section">
          <h2>Baby Management</h2>
          <div className="subsection">
            <h3>Active Baby</h3>
            <ActiveBabySelector user={user} onBabyChange={onUserUpdate} />
          </div>
          <div className="subsection">
            <h3>All Babies</h3>
            <BabyManagement user={user} />
          </div>
        </section>

        <section className="settings-section">
          <h2>Preferences</h2>
          <div className="setting-item">
            <div className="setting-info">
              <label htmlFor="time-format">Time Format</label>
              <p>Choose between 12-hour (AM/PM) or 24-hour time format</p>
            </div>
            <div className="time-format-toggle">
              <button
                className={`format-option ${!user.time_format_24h ? 'active' : ''}`}
                onClick={() => handleTimeFormatChange(false)}
                disabled={isUpdating}
              >
                12h (AM/PM)
              </button>
              <button
                className={`format-option ${user.time_format_24h ? 'active' : ''}`}
                onClick={() => handleTimeFormatChange(true)}
                disabled={isUpdating}
              >
                24h
              </button>
            </div>
          </div>
        </section>

        <section className="settings-section">
          <h2>Account Sharing</h2>
          <AccountSharing user={user} />
        </section>

        <section className="settings-section">
          <h2>Data Management</h2>
          <div className="data-management">
            <div className="data-action">
              <div className="action-info">
                <h3>Export Sleep Data</h3>
                <p>Download all sleep data for all your babies as a CSV file</p>
              </div>
              <button
                onClick={handleExportData}
                className="export-btn"
                disabled={isExporting}
              >
                <Download size={20} />
                {isExporting ? 'Exporting...' : 'Export Data'}
              </button>
            </div>

            {activeBaby && (
              <div className="data-action delete-action">
                <div className="action-info">
                  <h3>Delete Sleep Data</h3>
                  <p>
                    Delete all {sleepCount} sleep {sleepCount === 1 ? 'entry' : 'entries'} for{' '}
                    <strong>{activeBaby.name}</strong>
                  </p>
                  <p className="warning-text">This action cannot be undone</p>
                </div>
                <button
                  onClick={handleDeleteData}
                  className="delete-btn"
                  disabled={sleepCount === 0}
                >
                  <Trash size={20} />
                  Delete All Data
                </button>
              </div>
            )}
          </div>
        </section>

        <section className="settings-section settings-section-last">
          <button
            onClick={handleLogout}
            className="logout-btn"
          >
            Log Out
          </button>
        </section>
      </div>

      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Delete All Sleep Data</h2>
            <p>
              You are about to delete all {sleepCount} sleep {sleepCount === 1 ? 'entry' : 'entries'} for{' '}
              <strong>{activeBaby?.name}</strong>.
            </p>
            <p className="modal-warning">
              <strong>This action cannot be undone.</strong>
            </p>
            <p>Type <strong>DELETE</strong> to confirm:</p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Type DELETE"
              className="delete-confirm-input"
              autoFocus
            />
            <div className="modal-actions">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmText('');
                }}
                className="cancel-btn"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="confirm-delete-btn"
                disabled={deleteConfirmText !== 'DELETE' || isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete All Data'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showEmailModal && (
        <div className="modal-overlay" onClick={() => {
          setShowEmailModal(false);
          setEmail('');
          setVerificationCode('');
          setCodeRequested(false);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Add Email Address</h2>
            <p>
              Secure your account by adding an email address. You'll receive a verification code to confirm your email.
            </p>

            {!codeRequested ? (
              <>
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    autoFocus
                  />
                </div>
                <div className="modal-actions">
                  <button
                    onClick={() => {
                      setShowEmailModal(false);
                      setEmail('');
                    }}
                    className="cancel-btn"
                    disabled={isRequestingCode}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleRequestVerificationCode}
                    className="primary-btn"
                    disabled={!email.trim() || isRequestingCode}
                  >
                    {isRequestingCode ? 'Sending...' : 'Send Verification Code'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p>A verification code has been sent to <strong>{email}</strong></p>
                <div className="form-group">
                  <label htmlFor="code">Verification Code</label>
                  <input
                    type="text"
                    id="code"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    placeholder="Enter 10-digit code"
                    maxLength={10}
                    autoFocus
                  />
                </div>
                <div className="modal-actions">
                  <button
                    onClick={() => {
                      setCodeRequested(false);
                      setVerificationCode('');
                    }}
                    className="cancel-btn"
                    disabled={isVerifying}
                  >
                    Back
                  </button>
                  <button
                    onClick={handleVerifyEmail}
                    className="primary-btn"
                    disabled={!verificationCode.trim() || isVerifying}
                  >
                    {isVerifying ? 'Verifying...' : 'Verify Email'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;